
import { ApplicationInfo } from './../../../../../core/application-info/application-info.types';
import { ApplicationInfoService } from '@feature/core/application-info/application-info.service';
import { FuseConfigService } from '@fuse/services/config';
import { AppConfig } from 'app/core/config/app.config';
import { Component, Inject, OnInit, Renderer2 } from '@angular/core';
import { Layout } from '@feature/layout/layout.types';
import { combineLatest, filter, map, Observable, Subject, takeUntil } from 'rxjs';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { DOCUMENT } from '@angular/common';
import { FuseMediaWatcherService } from '@fuse/services/media-watcher';
import { FUSE_VERSION } from '@fuse/version';
import { ResponsiveBrandManagementService } from '@feature/core/responsive-brand/responsive-brand-management.service';
import * as brand from '../../../../../../../brand-themes';
import { NGXLogger } from 'ngx-logger';

@Component({
    selector: 'mojo-responsive-app-layout',
    templateUrl: './responsive-app-layout.component.html',
    styleUrls: ['./responsive-app-layout.component.scss'],
    standalone: false
})
export class ResponsiveAppLayoutComponent implements OnInit
  {
    config: AppConfig;
    layout: Layout;
    scheme: 'dark' | 'light';
    theme: string;
    private unsubscribeAll: Subject<any> = new Subject<any>();

    applicationInfo: Observable<ApplicationInfo>;

    brandedMenuImageSrc: string;

    /**
     * Constructor
     */

    constructor(
        private readonly logger: NGXLogger,
        brandService: ResponsiveBrandManagementService,
        private activatedRoute: ActivatedRoute,
        @Inject(DOCUMENT) private document: any,
        private renderer2: Renderer2,
        private router: Router,
        private fuseConfigService: FuseConfigService,
        private fuseMediaWatcherService: FuseMediaWatcherService,
        private appInfo: ApplicationInfoService
    ) {
        brandService.initialiseBrandManagementService();
    }

    settingsAccessEnabled(): boolean {
        return (localStorage.getItem('trace_system_settings') == 'true');
    }

    loadTemplateCss() : void {

        // Order dependant list of scss bundles configured in angular.json
        const bundleNames = [
                "resp-fuse-tailwind",
                "resp-fuse-themes",
                "resp-vendors",
                "resp-fuse-main",
                "resp-styles",
                "resp-tailwind"
        ];

        bundleNames.forEach(bundleName => {
            const lazyStyleElement = document.createElement('link');
            lazyStyleElement.rel = 'stylesheet';
            lazyStyleElement.href = `/${bundleName}.css`;
            document.body.appendChild(lazyStyleElement);
        });
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Lifecycle hooks
    // -----------------------------------------------------------------------------------------------------

    /**
     * On init
     */
    ngOnInit(): void
    {
        this.applicationInfo = this.appInfo.applicationInfo$;

        this.loadTemplateCss();

        // Set the theme and scheme based on the configuration
        combineLatest([
            this.fuseConfigService.config$,
            this.fuseMediaWatcherService.onMediaQueryChange$(['(prefers-color-scheme: dark)', '(prefers-color-scheme: light)'])
        ]).pipe(
            takeUntil(this.unsubscribeAll),
            map(([config, mql]) => {

                const options = {
                    scheme: config.scheme,
                    theme : config.theme
                };

                // If the scheme is set to 'auto'...
                if ( config.scheme === 'auto' )
                {
                    // Decide the scheme using the media query
                    options.scheme = mql.breakpoints['(prefers-color-scheme: dark)'] ? 'dark' : 'light';
                }

                return options;
            })
        ).subscribe((options) => {

            // Store the options
            this.scheme = options.scheme;
            this.theme = options.theme;

            // Update the scheme and theme
            this._updateScheme();
            this._updateTheme();
        });

        // Subscribe to config changes
        this.fuseConfigService.config$
            .pipe(takeUntil(this.unsubscribeAll))
            .subscribe((config: AppConfig) => {

                // Store the config
                this.config = config;

                // Update the layout
                this._updateLayout();
                this._updateTheme();
            });

        // Subscribe to NavigationEnd event
        this.router.events.pipe(
            filter(event => event instanceof NavigationEnd),
            takeUntil(this.unsubscribeAll)
        ).subscribe(() => {

            // Update the layout
            this._updateLayout();
        });

        // Set the app version
        this.renderer2.setAttribute(this.document.querySelector('[ng-version]'), 'fuse-version', FUSE_VERSION);
    }

    /**
     * On destroy
     */
    ngOnDestroy(): void
    {
        // Unsubscribe from all subscriptions
        this.unsubscribeAll.next(null);
        this.unsubscribeAll.complete();
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Private methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Update the selected layout
     */
    private _updateLayout(): void
    {


        // Get the current activated route
        let route = this.activatedRoute;
        while ( route.firstChild )
        {
            route = route.firstChild;
        }

        // 1. Set the layout from the config
        this.layout = this.config.layout;

        // 2. Get the query parameter from the current route and
        // set the layout and save the layout to the config
        const layoutFromQueryParam = (route.snapshot.queryParamMap.get('layout') as Layout);
        if ( layoutFromQueryParam )
        {
            this.layout = layoutFromQueryParam;
            if ( this.config )
            {
                this.config.layout = layoutFromQueryParam;
            }
        }

        // 3. Iterate through the paths and change the layout as we find
        // a config for it.
        //
        // The reason we do this is that there might be empty grouping
        // paths or componentless routes along the path. Because of that,
        // we cannot just assume that the layout configuration will be
        // in the last path's config or in the first path's config.
        //
        // So, we get all the paths that matched starting from root all
        // the way to the current activated route, walk through them one
        // by one and change the layout as we find the layout config. This
        // way, layout configuration can live anywhere within the path and
        // we won't miss it.
        //
        // Also, this will allow overriding the layout in any time so we
        // can have different layouts for different routes.
        const paths = route.pathFromRoot;
        paths.forEach((path) => {

            // Check if there is a 'layout' data
            if ( path.routeConfig && path.routeConfig.data && path.routeConfig.data.layout )
            {
                // Set the layout
                this.layout = path.routeConfig.data.layout;
            }
        });


    }

    /**
     * Update the selected scheme
     *
     * @private
     */
    private _updateScheme(): void
    {
        // Remove class names for all schemes
        this.document.body.classList.remove('light', 'dark');

        // Add class name for the currently selected scheme
        this.document.body.classList.add(this.scheme);
    }

    /**
     * Update the selected theme
     *
     * @private
     */
    private _updateTheme(): void
    {
        if ( this.config && this.config.theme ){
            this.logger.info(`setting theme to "${this.config.theme}".`)
            this.brandedMenuImageSrc = `assets/brand/brands/${brand.brandLogos[this.config.theme]}`;
        }

        // Find the class name for the previously selected theme and remove it
        this.document.body.classList.forEach((className: string) => {
            if ( className.startsWith('theme-') )
            {
                this.document.body.classList.remove(className, className.split('-')[1]);
            }
        });

        // Add class name for the currently selected theme
        this.document.body.classList.add(this.theme);
    }
}
